import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Theme: system / light / dark.
 *
 * Stored in localStorage rather than the IndexedDB `localdb`, because that one
 * is async — reading it would paint a dark screen first and then snap to
 * light. main.tsx calls `applyStoredTheme()` before React mounts so the very
 * first paint is already correct.
 *
 * The CSS contract: `:root` carries the dark palette, `:root[data-theme=...]`
 * overrides it. We always stamp the attribute explicitly, so there is never a
 * question of which palette is active.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'astrally.theme';
const SCALE_KEY = 'astrally.textScale';

/**
 * Text size steps, as a multiplier on the root font size.
 *
 * Scaling REM rather than applying a CSS `zoom` or a transform is deliberate:
 * every type size in global.css is already in rem, so this enlarges text
 * without disturbing the desktop nav rail, the fixed composer, or the native
 * safe-area insets — all of which a transform would break. Padding stays put,
 * which is the accessible behaviour: text grows, tap targets do not shrink,
 * and nothing reflows into a scrollbar it cannot escape.
 *
 * 1.4 is the ceiling because past it the six tab labels stop fitting on a
 * narrow phone even after truncation.
 */
export const TEXT_SCALES = [0.9, 1, 1.15, 1.3, 1.4] as const;
export const DEFAULT_TEXT_SCALE = 1;

export function readStoredScale(): number {
  try {
    const raw = Number(localStorage.getItem(SCALE_KEY));
    return (TEXT_SCALES as readonly number[]).includes(raw) ? raw : DEFAULT_TEXT_SCALE;
  } catch {
    return DEFAULT_TEXT_SCALE;
  }
}

export function applyTextScale(scale: number): void {
  document.documentElement.style.setProperty('--ui-scale', String(scale));
}

/** Background used for the browser chrome / native status bar, per theme. */
const CHROME_COLOR: Record<ResolvedTheme, string> = {
  dark: '#0b0b17',
  light: '#f6f5fb',
};

function systemPrefers(): ResolvedTheme {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

export function readStoredChoice(): ThemeChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
  } catch {
    // Private-mode Safari and some WebViews throw on localStorage access.
    return 'system';
  }
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemPrefers() : choice;
}

/** Native counterpart in app/android/.../SystemBarsPlugin.java. */
const SystemBars = registerPlugin<{ setLightIcons(o: { light: boolean }): Promise<void> }>(
  'SystemBars',
);

/**
 * Keep the system-bar icons readable. Android 15 draws the app edge-to-edge
 * behind transparent status and navigation bars, so their icons sit directly
 * on the page — in the light theme the default white icons would vanish
 * against a white background.
 */
async function syncNativeChrome(resolved: ResolvedTheme): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SystemBars.setLightIcons({ light: resolved === 'light' });
  } catch {
    // Web build, or an OEM that refuses the call. The page still renders
    // correctly; only the icon tint is left to the system.
  }
}

export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', CHROME_COLOR[resolved]);
  void syncNativeChrome(resolved);
}

/**
 * Called from main.tsx before the first render, so the first paint is already
 * the right theme AND the right text size — a resize after paint is far more
 * jarring than a colour change.
 */
export function applyStoredTheme(): void {
  applyTheme(resolveTheme(readStoredChoice()));
  applyTextScale(readStoredScale());
}

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
  /** Root font-size multiplier. See TEXT_SCALES. */
  textScale: number;
  setTextScale: (s: number) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  choice: 'system',
  resolved: 'dark',
  setChoice: () => undefined,
  textScale: DEFAULT_TEXT_SCALE,
  setTextScale: () => undefined,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStoredChoice());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredChoice()));
  const [textScale, setTextScaleState] = useState<number>(() => readStoredScale());

  const setTextScale = useCallback((next: number) => {
    const clamped = (TEXT_SCALES as readonly number[]).includes(next) ? next : DEFAULT_TEXT_SCALE;
    setTextScaleState(clamped);
    try {
      localStorage.setItem(SCALE_KEY, String(clamped));
    } catch {
      // Non-persistent is still better than not resizing at all.
    }
    applyTextScale(clamped);
  }, []);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-persistent is still better than not switching at all.
    }
    const r = resolveTheme(next);
    setResolved(r);
    applyTheme(r);
  }, []);

  // Follow the OS while the choice is "system" — including a change made
  // while the app is open, which is how Android's scheduled dark mode works.
  useEffect(() => {
    if (choice !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => {
      const r = systemPrefers();
      setResolved(r);
      applyTheme(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  const value = useMemo(
    () => ({ choice, resolved, setChoice, textScale, setTextScale }),
    [choice, resolved, setChoice, textScale, setTextScale],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
